import withRouter from "components/Common/withRouter";
import { ErrorMessage, useFormik } from "formik";
import PropTypes from "prop-types";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
//Import Breadcrumb
import ToastDialog from "components/Common/ToastDialog";
//redux
import { decryptedValue,  } from "common/utility";
import {
  editEntity,
  getOneEntity,
} from "http/fundLync/entityAndAddress.helper";
import { callFinancialCINSubmit, callGetProbeCreditRatings } from "http/fundLync/entityDocuments.helper";
import { getOneDeal } from "http/fundLync/deal.helper";
import { addCamData, editCamData, getCamData } from "http/fundLync/cam.helper";

const AboutSection = forwardRef((props, ref ) => {
  document.title = "About Entity | FundLync";
  const { queryString } = props; 
  const urlParams = new URLSearchParams(window.location.search);
  const encodedId = urlParams.get("id");
  const entityDetails = decryptedValue(localStorage.getItem("user"));
  const encodedDealId = urlParams.get("dealId");
  const entityId = atob(encodedId);
  const dealId = atob(encodedDealId);
  const [entity, setEntity] = useState([]);
  const [toastInfo, setToastInfo] = useState({ showToast: false });
  const [probeData, setProbeData] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newField, setNewField] = useState({ name: "", type: "" });
  const [aboutSectionData, setAboutSectionData] = useState([]);
  const [dealDetails, setDealDetails] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch the CAM data
        const response = await getCamData({ dealId });
        if (response?.status) {
          setAboutSectionData([response?.data[0]]);
        } else {
          const entityResponse = await getOneEntity({ entityId });
          setEntity(entityResponse?.data);

          const dealResponse = await getOneDeal({ id: dealId });
          setDealDetails(dealResponse);
          const probeResponse = await callGetProbeCreditRatings({ entityId });

          if (probeResponse?.status && probeResponse?.data?.description?.length > 0 && probeResponse?.data?.isManual == false) {
            setProbeData(probeResponse?.data);
          } else {
            setProbeData('');
          }
          setAboutSectionData([]); // Clear the About Section data
        }

      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadData();
  }, []);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setNewField({
        name: '',
        type: ''
      });
    }
  };


  const addNewField = () => {
    if (newField.name && newField.type) {
      setDynamicFields([...dynamicFields, newField]);
      setNewField({ name: "", type: "" });
      toggleModal();
    }
  };

  function capitalizeWords(str) {
    if (!str) return "";
    return str.toUpperCase();
  }

  const dynamicFieldsFromData = [
    ...(aboutSectionData[0]?.aboutSection?.dynamicFieldsData
      ? Object.entries(aboutSectionData[0].aboutSection.dynamicFieldsData).map(([key, value]) => ({
        name: key,
        type: value.type,
        value:value.value,
        fromExisting: true,
      }))
      : []),
    ...dynamicFields.map((field) => ({
      ...field,
      fromExisting: false,
    })),
  ];
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      entityName: aboutSectionData[0]?.aboutSection?.entityName || entity[0]?.entityName || "",
      companyDescription: aboutSectionData[0]?.aboutSection?.companyDescription || probeData?.description || "",
      loanRequirement: aboutSectionData[0]?.aboutSection?.loanRequirement || dealDetails?.loanRequirement || "",
      products:
        aboutSectionData[0]?.aboutSection?.products ||
        dealDetails?.dealProductCommercials?.map(item => item.product || "").join(", ") ||
        "",
      productTypes:
        aboutSectionData[0]?.aboutSection?.productTypes ||
        dealDetails?.dealProductCommercials?.map(item => item.productType || "").join(", ") ||
        "",
      creditRating:
        aboutSectionData[0]?.aboutSection?.creditRating ||
        (probeData?.credit_ratings?.[0]
          ? `${probeData.credit_ratings[0].rating_agency ? capitalizeWords(probeData.credit_ratings[0].rating_agency) : ""}${probeData.credit_ratings[0].rating_agency && probeData.credit_ratings[0].rating ? " / " : ""}${probeData.credit_ratings[0].rating || ""}`
          : "") ||
        "",
      // Include dynamic fields in initial values
      ...dynamicFieldsFromData.reduce((acc, field) => {
        acc[field.name] = field.value || "";
        return acc;
      }, {}),
    },
    validationSchema: Yup.object({
      entityName: Yup.string().required("Please Enter Entity Name"),
      companyDescription: Yup.string().required("Please Enter Company Description"),
      loanRequirement: Yup.string().required("Please Enter Loan Requirement"),
      products: Yup.string().required("Please Enter Product"),
      productTypes: Yup.string().required("Please Enter Product Type"),
      creditRating: Yup.string().required("Please Enter Credit Rating"),
      // Add validation for dynamic fields
      ...dynamicFieldsFromData.reduce((acc, field) => {
        acc[field.name] = Yup.string()
          .required(`Please Enter ${field.name}`)
          .test(
            'no-only-spaces',
            `${field.name} cannot be empty or only spaces`,
            (value) => value && value.trim() !== ''
          );
        return acc;
      }, {}),
    }),

    onSubmit: (values) => {
      if (aboutSectionData.length > 0) {
        const entity = {
          aboutSection: {
            entityName: values.entityName,
            companyDescription: values.companyDescription,
            loanRequirement: values.loanRequirement || "",
            products: values.products,
            productTypes: values.productTypes,
            creditRating: values.creditRating || "",
            dynamicFieldsData: dynamicFieldsFromData.reduce((acc, field) => {
              acc[field.name] = {
                value: values[field.name],  // Assuming `values` is defined elsewhere
                type: field.type,   // Assuming `values` contains a key for `field.type`
              };
              return acc;
            }, {}),
          },
          directorSharehodlerList: aboutSectionData[0]?.directorSharehodlerList,
          keyStrength: aboutSectionData[0]?.keyStrength,
          finSummary: aboutSectionData[0]?.finSummary,
          debtProfile: aboutSectionData[0]?.debtProfile,
          customerSupplier: aboutSectionData[0]?.customerSupplier,
          termsheet: aboutSectionData[0]?.termsheet,
          entityId: entityId,
          dealId: dealId,
          id: aboutSectionData[0]?.id,
          lastVisitedStep: 'list-of-directors' || null,
        }
        editCamData(entity)
          .then((response) => {
            if (response.code == 201 && response.status) {
              setToastInfo({
                message: "Cam Data Updated",
                showToast: true,
                role: "success",
              });
            } else {
              const { message } = response;
              setToastInfo({
                message: message,
                showToast: true,
                role: "error",
              });
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        const entity = {
          aboutSection: {
            entityName: values.entityName,
            companyDescription: values.companyDescription,
            loanRequirement: values.loanRequirement || "",
            products: values.products,
            productTypes: values.productTypes,
            creditRating: values.creditRating || "",
            dynamicFieldsData: dynamicFieldsFromData.reduce((acc, field) => {
              acc[field.name] = {
                value: values[field.name],  // Assuming `values` is defined elsewhere
                type: field.type,   // Assuming `values` contains a key for `field.type`
              };
              return acc;
            }, {}),
          },
          dealId: dealId,
          entityId: entityId,
          lastVisitedStep: 'list-of-directors' || null,
        };
        addCamData(entity)
          .then((response) => {
            if (response.code == 201 && response.status) {
              setToastInfo({
                message: "Cam Data Saved",
                showToast: true,
                role: "success",
              });
            } else {
              const { message } = response;
              setToastInfo({
                message: message,
                showToast: true,
                role: "error",
              });
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }

    },
  });
  useImperativeHandle(ref, () => ({
    handleSubmit: () => validation.handleSubmit(),
    toggleModal: () => toggleModal(),
    getValidationStatus: () =>
      validation.validateForm().then((errors) => Object.keys(errors).length === 0),
  }));

  const removeField = (fieldName) => {
    // Remove field from dynamic fields
    setDynamicFields((prevFields) =>
      prevFields.filter((field) => field.name !== fieldName)
    );

    // Remove field from aboutSectionData if present
    setAboutSectionData((prevData) => {
      if (prevData.length > 0) {
        const updatedDynamicFieldsData = { ...prevData[0]?.aboutSection?.dynamicFieldsData };
        delete updatedDynamicFieldsData[fieldName];
        return [
          {
            ...prevData[0],
            aboutSection: {
              ...prevData[0]?.aboutSection,
              dynamicFieldsData: updatedDynamicFieldsData,
            },
          },
        ];
      }
      return prevData;
    });

    // Update validation schema and form values
    validation.setFieldValue(fieldName, undefined);
    validation.setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[fieldName];
      return updatedErrors;
    });
  };



  return (
    <React.Fragment>
      <Row>
        <Col xs="12">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row>
              <Col sm="12" md="6">
                <Label className="required-sign form-label mt-4">Entity Name <i className="mdi mdi-asterisk"></i></Label>
                <Input
                  name="entityName"
                  type="text"
                  placeholder="Enter entity Name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.entityName || ""}
                  invalid={
                    validation.touched.entityName &&
                    validation.errors.entityName
                  }
                disabled
                />
                {validation.touched.entityName &&
                  validation.errors.entityName ? (
                  <FormFeedback type="invalid">
                    {validation.errors.entityName}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col sm="12" md="6">
                <Label className="required-sign form-label mt-4">Loan Amount <i className="mdi mdi-asterisk"></i></Label>
                <Input
                  name="loanRequirement"
                  type="text"
                  placeholder="Enter Loan Amount"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.loanRequirement || ""}
                  invalid={
                    validation.touched.loanRequirement &&
                    validation.errors.loanRequirement
                  }
                  disabled={aboutSectionData[0]?.aboutSection?.loanRequirement}
                />
                {validation.touched.loanRequirement &&
                  validation.errors.loanRequirement ? (
                  <FormFeedback type="invalid">
                    {validation.errors.loanRequirement}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col sm="12" md="6">
                <Label className="required-sign form-label mt-4">Product <i className="mdi mdi-asterisk"></i></Label>
                <Input
                  name="products"
                  type="text"
                  placeholder="Enter Loan Amount"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.products || ""}
                  invalid={
                    validation.touched.products &&
                    validation.errors.products
                  }
                // disabled
                />
                {validation.touched.products &&
                  validation.errors.products ? (
                  <FormFeedback type="invalid">
                    {validation.errors.products}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col sm="12" md="6">
                <Label className="required-sign form-label mt-4">Product Type <i className="mdi mdi-asterisk"></i></Label>
                <Input
                  name="productTypes"
                  type="text"
                  placeholder="Enter Loan Amount"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.productTypes || ""}
                  invalid={
                    validation.touched.productTypes &&
                    validation.errors.productTypes
                  }
                // disabled
                />
                {validation.touched.productTypes &&
                  validation.errors.productTypes ? (
                  <FormFeedback type="invalid">
                    {validation.errors.productTypes}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col sm="12" md="6">
                <Label className="required-sign form-label mt-4">Credit Rating <i className="mdi mdi-asterisk"></i></Label>
                <Input
                  name="creditRating"
                  type="text"
                  placeholder="Enter credit rating"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.creditRating || ""}
                  invalid={
                    validation.touched.creditRating &&
                    validation.errors.creditRating
                  }
                // disabled
                />
                {validation.touched.creditRating &&
                  validation.errors.creditRating ? (
                  <FormFeedback type="invalid">
                    {validation.errors.creditRating}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <div className="mt-4">
              <Col sm="12" md="12">
                <h5 className="text-success d-block entityDetailsValue mt-2 required-sign">
                  About the <span>{validation.values.entityName}</span> <i className="mdi mdi-asterisk"></i>
                </h5>
                <Input
                style={{textAlign: "justify"}}
                className="vertical-scroll"
                  name="companyDescription"
                  type="textarea"
                  placeholder="Company Description"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.companyDescription || ""}
                  rows="5"
                  invalid={
                    validation.touched.companyDescription &&
                    validation.errors.companyDescription
                  }
                // disabled
                />
                {validation.touched.companyDescription &&
                  validation.errors.companyDescription ? (
                  <FormFeedback type="invalid">
                    {validation.errors.companyDescription}
                  </FormFeedback>
                ) : null}
              </Col>
            </div>
            <Row className="mt-4 mb-4">
              {dynamicFieldsFromData.map((field, index) => (
                <Col sm="12" md={field.type === "textarea" ? "12" : "6"} key={index}>
                  <Label className="form-label mt-4 required-sign">
                    {field.name} <i className="mdi mdi-asterisk"></i>
                  </Label>
                  <div className="d-flex align-items-start">
                    <div style={{ flex: 1, position: "relative" }}>
                      <Input
                        name={field.name}
                        type={field.type}
                        placeholder={`Enter ${field.name}`}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values[field.name] || ""}
                        invalid={
                          validation.touched[field.name] && validation.errors[field.name]
                        }
                        className="vertical-scroll"
                      />
                      {validation.touched[field.name] && validation.errors[field.name] && (
                        <FormFeedback className="d-block">
                          {validation.errors[field.name]}
                        </FormFeedback>
                      )}
                    </div>
                    <i
                      className="mdi mdi-trash-can font-size-16 text-danger ms-2"
                      onClick={() => removeField(field.name)}
                      id="edittooltip"
                      style={{
                        cursor: "pointer",
                        alignSelf: "center",
                      }}
                    ></i>
                  </div>
                </Col>
              ))}
            </Row>
          </Form>
        </Col>
      </Row>
      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      ></ToastDialog>
      <Modal autoFocus={true} centered isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Add New Field</ModalHeader>
        <ModalBody>
          <Label>Field Name</Label>
          <Input
            type="text"
            placeholder="Enter Field Name"
            value={newField.name}
            onChange={(e) => setNewField({ ...newField, name: e.target.value })}
          />
          <Label className="mt-3">Field Type</Label>
          <Input
            type="select"
            value={newField.type}
            onChange={(e) => setNewField({ ...newField, type: e.target.value })}
          >
            <option value="">Select Type</option>
            <option value="text">Text Field</option>
            <option value="textarea">Textarea</option>
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-success save-customer" onClick={addNewField}>
            Add Field
          </Button>
          <button type="button" className="btn btn-outline-secondary save-customer me-2" onClick={toggleModal}>
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );

});

AboutSection.propTypes = {
  history: PropTypes.object,
};
AboutSection.displayName = "AboutSection"
export default AboutSection;
